import React, { PureComponent } from 'react';
import { compose, withApollo } from 'react-apollo';
import PropTypes from 'prop-types';
import {
  format,
  isAfter,
  isBefore,
  isValid,
  subDays,
  startOfMonth,
} from 'date-fns';

import { dictionaryItem, getDictionaryItem } from '../../hocs/withDictionary';

import AvailabilityPicker from './AvailabilityPicker';
import { Label, Select } from '../ui/Form';
import { Row, Col } from '../ui/Grid';
import { MessageWarning } from '../ui/Message';
import { WarningMessageIcon } from '../ui/Message/MessageWarning.style';
import { Text } from '../ui';

import withDatepicker from '../ui/Datepicker/hocs/withDatepicker';
import { DateInputWrapper, ValidationText } from './AvailabilityDuration.style';

import { DATE_FORMAT_INPUT } from '../../config/locale';
import { formatToHyphenFormat } from '../../lib/format';

import IbePropTypes from '../../IbePropTypes';
import StyledInputWithDateMask from './InputWithDateMask';
import { AvailabilityContext } from './AvailabilitySearchQuery';
import { mustBookAcrossPitches, parseAvailabilityTotalErrors } from './helpers';

import svgError from '../../static/images/icons/Error.svg';
import { SELECTED_DATES_STAY_STATUS } from './constants';
import theme from '../../styles/config/theme';

class AvailabilityDuration extends PureComponent {
  static propTypes = {
    apiError: PropTypes.bool.isRequired,
    availabilitySearchLoading: PropTypes.bool,
    availabilityTotalError: PropTypes.bool,
    datepicker: PropTypes.shape().isRequired,
    disabled: PropTypes.bool.isRequired,
    client: IbePropTypes.client,
    endDate: PropTypes.string,
    error: PropTypes.bool.isRequired,
    fetchAvailability: PropTypes.func.isRequired,
    fieldFocus: PropTypes.string,
    handleChange: PropTypes.func.isRequired,
    handleClear: PropTypes.func.isRequired,
    handleDatepickerChange: PropTypes.func.isRequired,
    handleFieldFocus: PropTypes.func.isRequired,
    handlePitchChange: PropTypes.func.isRequired,
    inputDateError: PropTypes.bool.isRequired,
    isSubmitted: PropTypes.bool.isRequired,
    loading: PropTypes.bool,
    memberBookableDate: PropTypes.string.isRequired,
    memberPricesAreSelected: PropTypes.bool.isRequired,
    nonMemberBookableDate: PropTypes.string.isRequired,
    notAvailableText: PropTypes.string,
    onCancelMemberPrices: PropTypes.func.isRequired,
    onChange: PropTypes.func.isRequired,
    onGoToMemberPrices: PropTypes.func.isRequired,
    onRequestText: PropTypes.string,
    options: PropTypes.shape({
      minDate: PropTypes.string,
      maxDate: PropTypes.string,
    }),
    pitches: PropTypes.arrayOf(PropTypes.shape(IbePropTypes.pitch)),
    pitchType: PropTypes.shape(),
    selectedEvent: PropTypes.shape(IbePropTypes.event),
    siteCode: PropTypes.string,
    startDate: PropTypes.string,
    selectedPitch: PropTypes.shape({
      id: PropTypes.string,
      restrictions: PropTypes.arrayOf(PropTypes.string),
    }),
    userIsMember: PropTypes.bool.isRequired,
  }

  static defaultProps = {
    availabilitySearchLoading: false,
    availabilityTotalError: false,
    client: {},
    loading: false,
    fieldFocus: '',
    notAvailableText: '',
    onRequestText: '',
    options: {
      minDate: '',
      maxDate: '',
    },
    pitches: [],
    pitchType: null,
    siteCode: null,
    selectedEvent: {},
    selectedPitch: {},
    startDate: null,
    endDate: null,
  }

  static contextType = AvailabilityContext;

  constructor(props) {
    super(props);

    this.state = {
      startDate: this.props.startDate ? format(this.props.startDate, DATE_FORMAT_INPUT) : '',
      endDate: this.props.endDate ? format(this.props.endDate, DATE_FORMAT_INPUT) : '',
    };
  }

  componentDidUpdate(prevProps, prevState) {
    if (
      prevProps.startDate !== this.props.startDate
      && format(this.props.startDate, DATE_FORMAT_INPUT) !== prevState.startDate) {
      this.updateDateState('startDate', this.props.startDate || '');
    }

    if (
      prevProps.endDate !== this.props.endDate
      && format(this.props.endDate, DATE_FORMAT_INPUT) !== prevState.endDate) {
      this.updateDateState('endDate', this.props.endDate || '');
    }
  }

  updateDateState = (name, value) => {
    const date = format(value, DATE_FORMAT_INPUT);
    this.setState({ [name]: date === 'Invalid Date' ? '' : date });
  }

  isValidDate = date => date.length === 10 && isValid(new Date(formatToHyphenFormat(date)));

  handleInputChange = ({ target: { name, value } }) => {
    const {
      handleChange,
      handleFieldFocus,
      fetchAvailability,
    } = this.props;

    const start = formatToHyphenFormat(this.state.startDate || this.props.startDate);
    const end = formatToHyphenFormat(this.state.endDate || this.props.endDate);
    const dateDurationArray = [start, end];

    this.setState({ [name]: value }, () => {
      if (this.isValidDate(value)) {
        dateDurationArray[name === 'startDate' ? 0 : 1] = formatToHyphenFormat(value);
        const [startDate, endDate] = dateDurationArray;
        let monthChanged = false;
        let startOfCalendarMonth;

        if (name === 'startDate') {
          const previousMonth = start.split('-')[1];
          const newMonth = startDate.split('-')[1];

          monthChanged = previousMonth !== newMonth;
          startOfCalendarMonth = format(startOfMonth(new Date(startDate)), 'YYYY-MM-DD');

          if (isAfter(startDate, endDate)) {
            dateDurationArray[1] = null;

            setTimeout(() => {
              handleFieldFocus('endDate');
              const input = document.getElementById('depart');
              input.focus();
            }, 0);
          }
        }

        if (name === 'endDate') {
          const previousMonth = end.split('-')[1];
          const newMonth = endDate.split('-')[1];

          monthChanged = previousMonth !== newMonth;
          startOfCalendarMonth = format(startOfMonth(new Date(endDate)), 'YYYY-MM-DD');

          if (isBefore(endDate, startDate)) {
            dateDurationArray[0] = format(subDays(new Date(endDate), 1), 'YYYY-MM-DD');

            setTimeout(() => {
              handleFieldFocus('startDate');
              const input = document.getElementById('arrive');
              input.focus();
            }, 0);
          }
        }

        if (monthChanged) {
          fetchAvailability({ startOfCalendarMonth });
        }

        handleChange(dateDurationArray);
      }
    });
  }

  handleCancelMemberPrices = () => {
    this.props.onCancelMemberPrices();
    this.props.handleClear();
  }

  handlePitchTypeChange = (value) => {
    const selectedPitchType = this.props.pitches.find(pitch => pitch.id === value);
    this.props.handlePitchChange(selectedPitchType);
  };

  render() {
    const {
      availabilitySearchLoading,
      datepicker,
      disabled,
      endDate,
      error,
      fetchAvailability,
      fieldFocus,
      handleChange,
      handleDatepickerChange,
      isSubmitted,
      loading,
      memberBookableDate,
      memberPricesAreSelected,
      nonMemberBookableDate,
      notAvailableText,
      onGoToMemberPrices,
      onRequestText,
      options,
      pitches,
      selectedEvent,
      selectedPitch,
      startDate,
      userIsMember,
    } = this.props;

    const { loading: availabilityLoading } = this.context;

    const parsedStartDate = this.isValidDate(this.state.startDate)
      ? formatToHyphenFormat(this.state.startDate)
      : startDate;
    const parsedEndDate = this.isValidDate(this.state.endDate)
      ? formatToHyphenFormat(this.state.endDate)
      : endDate;

    const hasDefinedDates = !!parsedStartDate && !!parsedEndDate;
    const hasEqualDates = parsedStartDate === parsedEndDate;

    const pickerProps = {
      datepicker,
      handleChange,
      handleDatepickerChange,
      startDate: parsedStartDate,
      endDate: parsedEndDate,
      hasEqualDates,
    };

    /**
     * Errors can come from three sources, an API validation error, the user failing to enter *
     * date, or when used with AvailabilityAmend an error within the availabilityTotal
     */

    const isSubmittedWithoutDates = isSubmitted && this.props.inputDateError;
    const startDateWithoutEndDate = this.isValidDate(this.state.startDate)
      && !this.isValidDate(this.state.endDate);
    const endDateWithoutStartDate = !this.isValidDate(this.state.startDate)
      && this.isValidDate(this.state.endDate);
    const validStartDateEqualsEndDate = (this.state.startDate === this.state.endDate
      && this.isValidDate(this.state.startDate));

    const isStartDateInputError = (
      this.props.inputDateError
      && endDateWithoutStartDate
    );
    const isEndDateInputError = (
      this.props.inputDateError
      && startDateWithoutEndDate
    );

    const isDateRangeError = this.props.apiError
    || isSubmittedWithoutDates
    || this.props.availabilityTotalError
    || validStartDateEqualsEndDate;

    const startDateInvalid = isStartDateInputError || isDateRangeError;
    const endDateInvalid = isEndDateInputError || isDateRangeError;

    const allPitchTypesDictionaryText = getDictionaryItem(this.props.client, 'AvailabilityDuration__AllPitchTypes__Label');

    const availabilityTotalPitches = pitches.map((pitch) => pitch.availabilityTotalPitchData) ?? [];

    const {
      siteFull,
      areSomePitchesFull,
      pitchFull: selectedPitchFull,
      siteHasDaysOnRequest,
      pitchHasDaysOnRequest,
    } = parseAvailabilityTotalErrors(
      availabilityTotalPitches,
      selectedPitch,
      memberPricesAreSelected,
    ) ?? {};

    // Check if the user must book across pitches to get their selected dates
    const stayStatus = mustBookAcrossPitches(
      availabilityTotalPitches,
      memberPricesAreSelected,
    );

    const userMustBookAcrossPitches =
      stayStatus === SELECTED_DATES_STAY_STATUS.MUST_BOOK_ACROSS_PITCHES;

    const hasValidDates = hasDefinedDates && !hasEqualDates;

    const isNeutralCalendar = !selectedPitch.id;

    const showRestrictions = !userMustBookAcrossPitches &&
      !isNeutralCalendar &&
      selectedPitch.restrictions?.length > 0;

    const showOnRequestError =
      !selectedPitch.restrictions?.length === 0 &&
      (isNeutralCalendar ? siteHasDaysOnRequest : pitchHasDaysOnRequest);

    const areSomeOfSelectedDatesFull = isNeutralCalendar
      ? areSomePitchesFull && !userMustBookAcrossPitches
      : selectedPitchFull;

    const showPitchFullError =
      !availabilityLoading &&
      !siteFull &&
      areSomeOfSelectedDatesFull &&
      hasValidDates &&
      !showOnRequestError &&
      !showRestrictions;

    const showSiteFullError =
      !availabilityLoading &&
      siteFull &&
      hasValidDates &&
      !showOnRequestError;

    const getPitchFullMessagingKey = () => {
      if (isNeutralCalendar) {
        return 'StandardNeutral';
      }
      if (userMustBookAcrossPitches) {
        return 'BookAcrossPitches';
      }
      return 'Standard';
    };

    const pitchErrorModifier = getPitchFullMessagingKey();

    return (
      <>
        {pitches && pitches.length > 0 && (
          <Row>
            <Col>
              <Label
                htmlFor="pitchTypeSelector"
                dictionary={dictionaryItem('AvailabilityDuration', 'PitchType')}
              />
              <Select
                block
                id="pitchTypeSelector"
                onChange={(e) => this.handlePitchTypeChange(e.target.value)}
                value={selectedPitch.id ?? ''}
                customStyled={{
                  border: `1px solid ${theme.COLOR_GRAY_DARK}`,
                }}
                data-pitch-type-wrapper
              >
                {pitches
                  .slice()
                  .sort((a, b) => {
                    const aPriority = a.prioritySortOrder ?? 0;
                    const bPriority = b.prioritySortOrder ?? 0;
                    if (aPriority === 0 && bPriority === 0) {
                      return a.name.localeCompare(b.name);
                    }
                    if (aPriority === 0) return 1;
                    if (bPriority === 0) return -1;
                    if (bPriority !== aPriority) {
                      return bPriority - aPriority;
                    }
                    return a.name.localeCompare(b.name);
                  })
                  .map(pitchType => (
                    <option key={pitchType.id} value={pitchType.id}>{pitchType.name}</option>
                  ))}
                <option value="">{allPitchTypesDictionaryText}</option>
              </Select>
            </Col>
          </Row>
        )}
        <Row>
          <Col>
            <Label htmlFor="arrive" dictionary={dictionaryItem('AvailabilityDuration', 'Arrive')} />
            <DateInputWrapper data-start-date-wrapper>
              <StyledInputWithDateMask
                id="arrive"
                name="startDate"
                onClick={e => this.props.handleFieldFocus(e.target.name)}
                onChange={this.handleInputChange}
                value={this.state.startDate}
                min={options.minDate}
                max={options.maxDate}
                error={startDateInvalid}
                inputStyle={{
                  border: `1px solid ${theme.COLOR_GRAY_DARK}`,
                }}
              />
            </DateInputWrapper>
            {isStartDateInputError && (
              <ValidationText
                align="left"
                dictionary={dictionaryItem('AvailabilityDuration', 'StartDateInputError')}
              />
            )}
          </Col>
          <Col>
            <Label htmlFor="depart" dictionary={dictionaryItem('AvailabilityDuration', 'Depart')} />
            <DateInputWrapper data-end-date-wrapper>
              <StyledInputWithDateMask
                id="depart"
                name="endDate"
                onClick={e => this.props.handleFieldFocus(e.target.name)}
                onChange={this.handleInputChange}
                value={this.state.endDate}
                min={options.minDate}
                max={options.maxDate}
                error={endDateInvalid}
                inputStyle={{
                  border: `1px solid ${theme.COLOR_GRAY_DARK}`,
                }}
              />

            </DateInputWrapper>
            {isEndDateInputError && (
              <ValidationText
                align="right"
                dictionary={dictionaryItem('AvailabilityDuration', 'EndDateInputError')}
              />
            )}
          </Col>
        </Row>

        <AvailabilityPicker
          {...pickerProps}
          availabilitySearchLoading={availabilitySearchLoading}
          disabled={disabled}
          fullscreen={false}
          error={error}
          fetchAvailability={fetchAvailability}
          fieldFocus={fieldFocus}
          loading={loading}
          memberBookableDate={memberBookableDate}
          memberPricesAreSelected={memberPricesAreSelected}
          nonMemberBookableDate={nonMemberBookableDate}
          notAvailableText={notAvailableText}
          onGoToMemberPrices={onGoToMemberPrices}
          onRequestText={onRequestText}
          selectedEvent={selectedEvent}
          selectedPitch={selectedPitch}
          userIsMember={userIsMember}
          onCancelMemberPrices={this.handleCancelMemberPrices}
          pitches={pitches}
        />

        {showRestrictions &&
          selectedPitch.restrictions.map(restriction => (
            <MessageWarning
              marginTop
              key={restriction}
            >
              {restriction}
            </MessageWarning>
          ))
        }

        {showOnRequestError && hasValidDates &&
          <MessageWarning marginTop dictionary={dictionaryItem('AvailabilityDuration__OnRequestError')} />
        }

        {showSiteFullError &&
          <MessageWarning marginTop dictionary={dictionaryItem('AvailabilityDuration__SiteFullSelectedError')} />
        }

        {showPitchFullError &&
          <MessageWarning
            marginTop
            dictionary={dictionaryItem(`AvailabilityDuration__PitchFullError__${pitchErrorModifier}`)}
          />
        }

        {userMustBookAcrossPitches && isNeutralCalendar &&
          <MessageWarning flex marginTop>
            <WarningMessageIcon icon={svgError} />
            <Text dictionary={dictionaryItem('AvailabilityDuration', 'MustBookAcrossPitchesError')} span />
          </MessageWarning>
        }
      </>
    );
  }
}

export default compose(withDatepicker, withApollo)(AvailabilityDuration);
